<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import TeamsAPI from 'dashboard/api/teams';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});

// Search and team filter
const searchQuery = ref('');
const selectedTeamId = ref(null);
const allTeams = ref([]);
const teamAgentMap = ref({});

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);
const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

// Filtered agent list based on search and team filter
const filteredAgentList = computed(() => {
  let list = agentList.value;

  // Filter by search query (name or email)
  const query = searchQuery.value.trim().toLowerCase();
  if (query) {
    list = list.filter(
      agent =>
        (agent.name || '').toLowerCase().includes(query) ||
        (agent.email || '').toLowerCase().includes(query)
    );
  }

  // Filter by selected team
  if (selectedTeamId.value) {
    const teamMembers = teamAgentMap.value[selectedTeamId.value] || [];
    list = list.filter(agent => teamMembers.includes(agent.id));
  }

  return list;
});

// Fetch teams and their members for the filter
const loadTeamsForFilter = async () => {
  try {
    const { data: teams } = await TeamsAPI.get(true);
    allTeams.value = teams || [];

    // Build map of teamId -> [agentIds]
    const memberPromises = (teams || []).map(async team => {
      try {
        const { data: members } = await TeamsAPI.getAgents({
          teamId: team.id,
        });
        return { teamId: team.id, agentIds: members.map(m => m.id) };
      } catch {
        return { teamId: team.id, agentIds: [] };
      }
    });

    const results = await Promise.all(memberPromises);
    const map = {};
    results.forEach(({ teamId, agentIds }) => {
      map[teamId] = agentIds;
    });
    teamAgentMap.value = map;
  } catch {
    // Teams fetch failed — filter will be empty
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
  loadTeamsForFilter();
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

// Account owner: first administrator (by lowest ID) excluding user_id=1 (SuperAdmin)
const accountOwnerId = computed(() => {
  const admins = agentList.value
    .filter(a => a.role === 'administrator' && a.id !== 1)
    .sort((a, b) => a.id - b.id);
  return admins.length ? admins[0].id : null;
});

const canToggleAccess = agent => {
  // Can't toggle self
  if (currentUserId.value === agent.id) return false;
  // Can't toggle account owner
  if (agent.id === accountOwnerId.value) return false;
  return true;
};

const toggleAccessLock = async agent => {
  if (!canToggleAccess(agent)) return;
  const willLock = !agent.access_locked;
  try {
    await store.dispatch('agents/update', {
      id: agent.id,
      access_locked: willLock,
    });
    useAlert(
      willLock
        ? t('AGENT_MGMT.SEARCH.ACCESS_LOCKED_SUCCESS')
        : t('AGENT_MGMT.SEARCH.ACCESS_UNLOCKED_SUCCESS')
    );
  } catch {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="!agentList.length"
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AGENT_MGMT.HEADER')"
        :description="$t('AGENT_MGMT.DESCRIPTION')"
        :link-text="$t('AGENT_MGMT.LEARN_MORE')"
        feature-name="agents"
        tooltip-identifier="agents_settings"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('AGENT_MGMT.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <!-- Search and Team Filter -->
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
        <div class="relative flex-1">
          <span
            class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-n-slate-10"
          >
            <fluent-icon icon="search" size="16" />
          </span>
          <input
            v-model="searchQuery"
            type="text"
            class="!mb-0 !pl-9 !pr-3 !py-2 !text-sm !rounded-lg !bg-n-alpha-black2 !outline-1 !outline-n-weak hover:!outline-n-slate-6 !border-0"
            :placeholder="$t('AGENT_MGMT.SEARCH.PLACEHOLDER')"
          />
        </div>
        <div class="sm:w-56">
          <select
            v-model="selectedTeamId"
            class="!mb-0 !py-2 !text-sm !rounded-lg"
          >
            <option :value="null">
              {{ $t('AGENT_MGMT.SEARCH.ALL_TEAMS') }}
            </option>
            <option
              v-for="team in allTeams"
              :key="team.id"
              :value="team.id"
            >
              {{ team.name }}
            </option>
          </select>
        </div>
      </div>

      <!-- No results message -->
      <p
        v-if="filteredAgentList.length === 0 && agentList.length > 0"
        class="text-sm text-n-slate-10 text-center py-6"
      >
        {{ $t('AGENT_MGMT.SEARCH.NO_RESULTS') }}
      </p>

      <table v-else class="divide-y divide-n-weak">
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <tr v-for="(agent, index) in filteredAgentList" :key="agent.email">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-row items-center gap-4">
                <Avatar
                  :src="agent.thumbnail"
                  :name="agent.name"
                  :status="agent.availability_status"
                  :size="40"
                  hide-offline-status
                  rounded-full
                />
                <div>
                  <span class="block font-medium capitalize">
                    {{ agent.name }}
                  </span>
                  <span>{{ agent.email }}</span>
                </div>
              </div>
            </td>

            <td class="relative py-4 ltr:pr-4 rtl:pl-4">
              <span
                class="block font-medium w-fit"
                :class="{
                  'hover:text-gray-900 group cursor-pointer':
                    agent.custom_role_id,
                }"
              >
                {{ getAgentRoleName(agent) }}

                <div
                  class="absolute left-0 z-10 hidden max-w-[300px] w-auto bg-white rounded-xl border border-n-weak shadow-lg top-14 md:top-12 dark:bg-n-solid-2"
                  :class="{ 'group-hover:block': agent.custom_role_id }"
                >
                  <div class="flex flex-col gap-1 p-4">
                    <span class="font-semibold">
                      {{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}
                    </span>
                    <ul class="pl-4 mb-0 list-disc">
                      <li
                        v-for="permission in getAgentRolePermissions(agent)"
                        :key="permission"
                        class="font-normal"
                      >
                        {{
                          $t(
                            `CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`
                          )
                        }}
                      </li>
                    </ul>
                  </div>
                </div>
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span v-if="agent.confirmed">
                {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
              </span>
              <span v-if="!agent.confirmed">
                {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                  :class="[
                    !agent.access_locked
                      ? 'bg-woot-500'
                      : 'bg-n-slate-6',
                    !canToggleAccess(agent)
                      ? 'opacity-50 cursor-not-allowed'
                      : '',
                  ]"
                  :disabled="!canToggleAccess(agent)"
                  @click="toggleAccessLock(agent)"
                >
                  <span
                    class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    :class="
                      !agent.access_locked
                        ? 'translate-x-5'
                        : 'translate-x-0'
                    "
                  />
                </button>
                <span
                  class="text-xs"
                  :class="
                    agent.access_locked
                      ? 'text-n-ruby-9'
                      : 'text-n-teal-9'
                  "
                >
                  {{ agent.access_locked ? $t('AGENT_MGMT.SEARCH.LOCKED') : $t('AGENT_MGMT.SEARCH.ACTIVE') }}
                </span>
              </div>
            </td>
            <td class="py-4">
              <div class="flex justify-end gap-1">
                <Button
                  v-if="showEditAction(agent)"
                  v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  @click="openEditPopup(agent)"
                />
                <Button
                  v-if="showDeleteAction(agent)"
                  v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loading[agent.id]"
                  @click="openDeletePopup(agent, index)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :provider="currentAgent.provider"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :availability="currentAgent.availability_status"
        :custom-role-id="currentAgent.custom_role_id"
        :custom-attributes="currentAgent.custom_attributes || {}"
        :access-locked="currentAgent.access_locked || false"
        :is-account-owner="currentAgent.id === accountOwnerId"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </SettingsLayout>
</template>
